import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Box,
  Skeleton,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  LocalOffer as BrandIcon,
  AttachMoney as SalesIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const Reports = () => {
  // 状態管理
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ページネーション
  const [accessPage, setAccessPage] = useState(0);
  const [accessTotal, setAccessTotal] = useState(0);
  const [errorPage, setErrorPage] = useState(0);
  const [errorTotal, setErrorTotal] = useState(0);
  const rowsPerPage = 10;

  // データ取得
  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get(`/api/system/reports/summary?period=${period}`);
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (err) {
      console.error("サマリー取得エラー:", err);
      setError("サマリーの取得に失敗しました");
    }
  }, [period]);

  const fetchAccessLogs = useCallback(async () => {
    try {
      const response = await api.get(
        `/api/system/reports/access-logs?period=${period}&limit=${rowsPerPage}&offset=${accessPage * rowsPerPage}`
      );
      if (response.data.success) {
        setAccessLogs(response.data.data.logs);
        setAccessTotal(response.data.data.total);
      }
    } catch (err) {
      console.error("アクセスログ取得エラー:", err);
    }
  }, [period, accessPage]);

  const fetchErrorLogs = useCallback(async () => {
    try {
      const response = await api.get(
        `/api/system/reports/error-logs?period=${period}&limit=${rowsPerPage}&offset=${errorPage * rowsPerPage}`
      );
      if (response.data.success) {
        setErrorLogs(response.data.data.logs);
        setErrorTotal(response.data.data.total);
      }
    } catch (err) {
      console.error("エラーログ取得エラー:", err);
    }
  }, [period, errorPage]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchSummary(), fetchAccessLogs(), fetchErrorLogs()]);
    setLoading(false);
  }, [fetchSummary, fetchAccessLogs, fetchErrorLogs]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 期間変更時
  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
    setAccessPage(0);
    setErrorPage(0);
  };

  // CSVダウンロード
  const handleDownload = async (type) => {
    try {
      const response = await api.get(
        `/api/system/reports/export/${type}?period=${period}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type}-${period}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("ダウンロードエラー:", err);
      setError("CSVダウンロードに失敗しました");
    }
  };

  // 日時フォーマット
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // 期間ラベル
  const getPeriodLabel = () => {
    const labels = {
      today: "今日",
      week: "今週",
      month: "今月",
      year: "今年",
    };
    return labels[period] || period;
  };

  // サマリーカード
  const SummaryCard = ({ icon: Icon, title, value, color, suffix = "" }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Icon sx={{ color, mr: 1 }} />
          <Typography color="textSecondary" variant="body2">
            {title}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton width={80} height={40} />
        ) : (
          <Typography variant="h4" sx={{ color }}>
            {value?.toLocaleString() || 0}
            {suffix && <Typography component="span" variant="body1" sx={{ ml: 0.5 }}>{suffix}</Typography>}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        {/* ヘッダー */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" component="h1">
              システムレポート
            </Typography>
            <Tooltip title="データを更新">
              <IconButton onClick={fetchAllData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* エラー表示 */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Grid>
        )}

        {/* レポート期間選択 */}
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>レポート期間</InputLabel>
            <Select value={period} onChange={handlePeriodChange} label="レポート期間">
              <MenuItem value="today">今日</MenuItem>
              <MenuItem value="week">今週</MenuItem>
              <MenuItem value="month">今月</MenuItem>
              <MenuItem value="year">今年</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* 概要カード */}
        <Grid item xs={6} sm={4} md={3}>
          <SummaryCard
            icon={BusinessIcon}
            title="総企業数"
            value={summary?.companyCount}
            color="#1976d2"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <SummaryCard
            icon={PeopleIcon}
            title="総ユーザー数"
            value={summary?.userCount}
            color="#388e3c"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <SummaryCard
            icon={StoreIcon}
            title="総店舗数"
            value={summary?.storeCount}
            color="#f57c00"
          />
        </Grid>

        <Grid item xs={6} sm={4} md={3}>
          <SummaryCard
            icon={BrandIcon}
            title="総ブランド数"
            value={summary?.brandCount}
            color="#7b1fa2"
          />
        </Grid>

        <Grid item xs={12} sm={8} md={6}>
          <SummaryCard
            icon={SalesIcon}
            title={`${getPeriodLabel()}の売上総額`}
            value={summary?.totalSales}
            color="#c62828"
            suffix="円"
          />
        </Grid>

        {/* システムアクセスログ */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">
                システムアクセスログ
                <Chip
                  label={`${accessTotal}件`}
                  size="small"
                  sx={{ ml: 1 }}
                  color="primary"
                  variant="outlined"
                />
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日時</TableCell>
                    <TableCell>ユーザー</TableCell>
                    <TableCell>アクション</TableCell>
                    <TableCell>対象</TableCell>
                    <TableCell>IPアドレス</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(5)].map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : accessLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">
                          {getPeriodLabel()}のアクセスログはありません
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    accessLogs.map((log, index) => (
                      <TableRow key={log.id || log._id || index} hover>
                        <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                        <TableCell>{log.user_login || log.userLogin || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            variant="outlined"
                            color={log.action?.toLowerCase().includes("login") ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>{log.entity || "-"}</TableCell>
                        <TableCell>{log.ip || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={accessTotal}
              page={accessPage}
              onPageChange={(e, newPage) => setAccessPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
            />
            <CardActions>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchAccessLogs}
                disabled={loading}
              >
                更新
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload("access-logs")}
                disabled={loading}
              >
                CSVダウンロード
              </Button>
            </CardActions>
          </Paper>
        </Grid>

        {/* エラーログ */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">
                <ErrorIcon sx={{ verticalAlign: "middle", mr: 1, color: "error.main" }} />
                エラーログ
                <Chip
                  label={`${errorTotal}件`}
                  size="small"
                  sx={{ ml: 1 }}
                  color="error"
                  variant="outlined"
                />
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日時</TableCell>
                    <TableCell>アクション</TableCell>
                    <TableCell>対象</TableCell>
                    <TableCell>詳細</TableCell>
                    <TableCell>IPアドレス</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(5)].map((_, j) => (
                          <TableCell key={j}><Skeleton /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : errorLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary">
                          {getPeriodLabel()}のエラーログはありません
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    errorLogs.map((log, index) => (
                      <TableRow key={log.id || log._id || index} hover>
                        <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{log.entity || "-"}</TableCell>
                        <TableCell sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                          {typeof log.details === "object"
                            ? JSON.stringify(log.details)
                            : log.details || "-"}
                        </TableCell>
                        <TableCell>{log.ip || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={errorTotal}
              page={errorPage}
              onPageChange={(e, newPage) => setErrorPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
            />
            <CardActions>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchErrorLogs}
                disabled={loading}
              >
                更新
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload("error-logs")}
                disabled={loading}
              >
                CSVダウンロード
              </Button>
            </CardActions>
          </Paper>
        </Grid>

        {/* フッター情報 */}
        {summary && (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              集計期間: {formatDateTime(summary.startDate)} 〜 {formatDateTime(summary.endDate)}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default Reports;
